export type CustomerDateFormat = 'UsMonthDayYear' | 'UkDayMonthYear' | 'IsoYearMonthDay';

export type CustomerNumberFormat = 'UsCommaDot' | 'EuropeanDotComma' | 'FrenchCanadianSpaceComma';

export type CustomerLocaleSource = 'AddressCountry' | 'OrgDefault' | 'UsFallback';

/**
 * Explicit customer locale bag for document preview / PDF rendering.
 * Must come from address country (or org/US fallback) — never signed-in user prefs.
 */
export type CustomerLocale = {
  dateFormat: CustomerDateFormat;
  numberFormat: CustomerNumberFormat;
  timeZone: string;
  cultureTag: string;
  source: CustomerLocaleSource;
};

export const DEFAULT_CUSTOMER_LOCALE: CustomerLocale = {
  dateFormat: 'UsMonthDayYear',
  numberFormat: 'UsCommaDot',
  timeZone: 'UTC',
  cultureTag: 'en-US',
  source: 'UsFallback'
};

const DATE_FORMATS = new Set<string>(['UsMonthDayYear', 'UkDayMonthYear', 'IsoYearMonthDay']);
const NUMBER_FORMATS = new Set<string>(['UsCommaDot', 'EuropeanDotComma', 'FrenchCanadianSpaceComma']);
const SOURCES = new Set<string>(['AddressCountry', 'OrgDefault', 'UsFallback']);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Light validation for host/API bags; returns undefined when required fields are missing/invalid. */
export function parseCustomerLocale(raw: unknown): CustomerLocale | undefined {
  if (!isPlainObject(raw)) return undefined;

  const { dateFormat, numberFormat, timeZone, cultureTag, source } = raw;
  if (typeof dateFormat !== 'string' || !DATE_FORMATS.has(dateFormat)) return undefined;
  if (typeof numberFormat !== 'string' || !NUMBER_FORMATS.has(numberFormat)) return undefined;
  if (typeof timeZone !== 'string' || !timeZone.trim()) return undefined;
  if (typeof cultureTag !== 'string' || !cultureTag.trim()) return undefined;

  return {
    dateFormat: dateFormat as CustomerDateFormat,
    numberFormat: numberFormat as CustomerNumberFormat,
    timeZone: timeZone.trim(),
    cultureTag: cultureTag.trim(),
    source: typeof source === 'string' && SOURCES.has(source) ? (source as CustomerLocaleSource) : 'UsFallback'
  };
}
